import { createFileRoute } from "@tanstack/react-router";
import { useStore, getReportRows } from "@/lib/pos-store";
import { fmt } from "@/components/AppShell";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printHtml, fmtNum, fmtQty } from "@/lib/print-utils";

export const Route = createFileRoute("/reports/")({
  head: () => ({
    meta: [
      { title: "Sales Sheet" },
      {
        name: "description",
        content: "Per-item daily sales sheet with totals.",
      },
    ],
  }),
  component: SalesSheet,
});

function SalesSheet() {
  const state = useStore((s) => s);
  const rows = getReportRows(state);
  const t = rows.reduce(
    (a, r) => ({
      openStock: a.openStock + r.openStock,
      addStock: a.addStock + r.addStock,
      totalStock: a.totalStock + r.totalStock,
      closeStock: a.closeStock + r.closeStock,
      totalSale: a.totalSale + r.totalSale,
      totalProfit: a.totalProfit + r.totalProfit,
      totalAmount: a.totalAmount + r.totalAmount,
      netProfit: a.netProfit + r.netProfit,
    }),
    {
      openStock: 0,
      addStock: 0,
      totalStock: 0,
      closeStock: 0,
      totalSale: 0,
      totalProfit: 0,
      totalAmount: 0,
      netProfit: 0,
    },
  );

  const handlePrint = () => {
    const tableRows = rows
      .map(
        (r) => `
      <tr>
        <td>${r.idx}</td>
        <td>${r.item}</td>
        <td class="num">${fmtQty(r.openStock)}</td>
        <td class="num">${fmtQty(r.addStock)}</td>
        <td class="num">${fmtQty(r.totalStock)}</td>
        <td class="num">${fmtQty(r.closeStock)}</td>
        <td class="num">${fmtQty(r.totalSale)}</td>
        <td class="num">${fmtNum(r.buyingPrice)}</td>
        <td class="num">${fmtNum(r.salePrice)}</td>
        <td class="num">${fmtNum(r.totalProfit)}</td>
        <td class="num">${fmtNum(r.totalAmount)}</td>
        <td class="num">${fmtNum(r.netProfit)}</td>
      </tr>`,
      )
      .join("");

    const body = `
      <h1>Sales Sheet</h1>
      <div class="subtitle">${new Date().toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      <table>
        <thead>
          <tr>
            <th>S/no</th><th>Item</th><th>Open</th><th>Add</th><th>Total</th><th>Close</th><th>Sold</th><th>Buy</th><th>Sale</th><th>Profit</th><th>Amount</th><th>Net</th>
          </tr>
        </thead>
        <tbody>
          ${tableRows}
          <tr class="total-row">
            <td></td><td>TOTALS</td>
            <td class="num">${fmtQty(t.openStock)}</td>
            <td class="num">${fmtQty(t.addStock)}</td>
            <td class="num">${fmtQty(t.totalStock)}</td>
            <td class="num">${fmtQty(t.closeStock)}</td>
            <td class="num">${fmtQty(t.totalSale)}</td>
            <td class="num">—</td>
            <td class="num">—</td>
            <td class="num">${fmtNum(t.totalProfit)}</td>
            <td class="num">${fmtNum(t.totalAmount)}</td>
            <td class="num">${fmtNum(t.netProfit)}</td>
          </tr>
        </tbody>
      </table>
      <div class="section-title">Summary</div>
      <div class="summary-line"><span>Gross Sales</span><span>${fmtNum(t.totalAmount)}</span></div>
      <div class="summary-line total"><span>Net Profit</span><span>${fmtNum(t.netProfit)}</span></div>`;

    printHtml("Sales Sheet", body);
  };

  return (
    <div className="px-2 pt-3">
      <div className="mb-3 flex justify-end px-2">
        <Button variant="outline" size="sm" onClick={handlePrint}>
          <Printer className="h-3.5 w-3.5" /> Print
        </Button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[920px] text-[11px]">
          <thead className="bg-secondary text-secondary-foreground">
            <tr>
              {[
                "S/no",
                "Item",
                "Open",
                "Add",
                "Total",
                "Close",
                "Sold",
                "Buy",
                "Sale",
                "Profit",
                "Amount",
                "Net",
              ].map((h) => (
                <th key={h} className="px-2 py-2 text-left font-semibold">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.idx} className="bg-card">
                <td className="px-2 py-2">{r.idx}</td>
                <td className="px-2 py-2 font-medium">{r.item}</td>
                <td className="px-2 py-2">{r.openStock}</td>
                <td className="px-2 py-2">{r.addStock}</td>
                <td className="px-2 py-2">{r.totalStock}</td>
                <td className="px-2 py-2">{r.closeStock}</td>
                <td className="px-2 py-2">{r.totalSale}</td>
                <td className="px-2 py-2">{r.buyingPrice}</td>
                <td className="px-2 py-2">{r.salePrice}</td>
                <td className="px-2 py-2 text-primary">{r.totalProfit}</td>
                <td className="px-2 py-2 font-semibold">{r.totalAmount}</td>
                <td className="px-2 py-2 text-primary">{r.netProfit}</td>
              </tr>
            ))}
            <tr className="bg-accent font-semibold text-accent-foreground">
              <td className="px-2 py-2"></td>
              <td className="px-2 py-2">TOTALS</td>
              <td className="px-2 py-2">{t.openStock}</td>
              <td className="px-2 py-2">{t.addStock}</td>
              <td className="px-2 py-2">{t.totalStock}</td>
              <td className="px-2 py-2">{t.closeStock}</td>
              <td className="px-2 py-2">{t.totalSale}</td>
              <td className="px-2 py-2">—</td>
              <td className="px-2 py-2">—</td>
              <td className="px-2 py-2">{t.totalProfit}</td>
              <td className="px-2 py-2">{t.totalAmount}</td>
              <td className="px-2 py-2">{t.netProfit}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <Stat label="Gross Sales" value={fmt(t.totalAmount)} />
        <Stat label="Net Profit" value={fmt(t.netProfit)} accent />
      </div>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        Swipe the table horizontally to see all columns
      </p>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${accent ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}
    >
      <p className="text-xs uppercase tracking-wider opacity-70">{label}</p>
      <p className="font-display text-lg font-bold">{value}</p>
    </div>
  );
}
